import React from 'react'
import { useCallback, useMedia, useMemo, useQuery, useQueryOptionsType } from 'sb-hooks'
import { FormattedMessage } from 'sb-react-intl'
import { useConnect } from 'sb-redux-hook'
import actions from 'redux/actions'
import { constants } from 'common/helpers'

import Modal from 'components/modal/Modal/Modal'
import Product from 'components/product/Product/Product'
import { Button, buttonMessages } from 'components/_new/controls'
import { ContentLoader } from 'components/_new/ui'

import messages from './messages'

import noteProductsQuery from './util/noteProducts.graphql'

import s from './NoteRecommendationsModal.scss'


type NoteRecommendationsModalProps = {
  name: string
  data: {
    addTo: string
    note: {
      id: number
      name: string
    }
  }
}

const NoteRecommendationsModal: React.FunctionComponent<NoteRecommendationsModalProps> = (props) => {
  const { name = constants.modals.NoteRecommendations, data: { note, addTo } } = props
  const { id: noteId } = note

  const { isMobile } = useMedia()

  const { excludeProductId } = useConnect({
    excludeProductId: 'view.productPage.id',
  })

  const queryParams = useMemo<useQueryOptionsType>(() => ({
    name: 'noteRecommendationsModal',
    variables: {
      noteId,
      excludeProductId,
      limit: 6,
    },
    modifyResult: ({ noteById: { products } }) => products,
  }), [ noteId, excludeProductId ])

  const { data: products, isFetching } = useQuery(noteProductsQuery, queryParams)

  const handleClose = useCallback(() => {
    actions.models.modals.close(name)
  }, [ name ])

  const [ metadata, snowplowMetadata ] = useMemo(() => ([
    { placement: 'note popup' },
    { placement: 'note recommendations modal' }, // TODO SNOWPLOW check if correct
  ]), [])

  let content

  if (isFetching) {
    content = (
      <ContentLoader />
    )
  }
  else if (!products || !products.length) {
    content = (
      <FormattedMessage className={s.empty} tag="div" {...messages.empty} />
    )
  }
  else {
    content = (
      <div className={s.items}>
        {
          products.map((item) => (
            <div key={item.id} className={s.item} onClick={handleClose}>
              <Product
                containerClassName={s.product}
                data={item}
                addTo={addTo}
                metadata={metadata}
                snowplowMetadata={snowplowMetadata}
                isQuickViewVisible
                onQuickViewClick={handleClose}
              />
            </div>
          ))
        }
      </div>
    )
  }

  return (
    <Modal
      className={s.modal}
      name={name}
      titleNode={(
        <FormattedMessage
          tag="div"
          className={s.title}
          {...messages.title}
          values={{ note: note.name.toLowerCase() }}
        />
      )}
      onClose={handleClose}
    >
      {content}
      {
        isMobile && (
          <Button
            className={s.button}
            title={buttonMessages.close}
            color="transparent"
            size="md"
            fullWidth
            dataTestId="closeButton"
            onClick={handleClose}
          />
        )
      }
    </Modal>
  )
}


export default NoteRecommendationsModal
